import { FC } from 'react';
import Swiper, { SwiperSlide } from '@/components/common/Swiper';
import ImpTracker from '@/components/common/ImpTracker';
import { getPageEventNameV2 } from 'tracks/tracker';
import { getPathNoLangOnClient, toString as queryToString } from '@/utils/url';
import useImpressionT2 from '@/tracks/useImpressionT2';
import { CategoryMod } from '@/constants/track';
import { useRouter } from 'next/router';
import useClickActionT2 from '@/tracks/useClickActionT2';
import { CroppedImage } from '@weee-fe/module';
import { CategoryType, CuisineCategory } from '@/types/category';
import NewTag from '@/components/category/NewTag';
import RewardsDealTag from '@/components/rewards/RewardsDealTag';
import isEmpty from '@/utils/isEmpty';
import { twMerge } from 'tailwind-merge';
import useTranslation from 'next-translate/useTranslation';
import Link from 'next/link';
import classnames from 'classnames';

interface IProps {
  loadingMain: boolean;
  mainCategoryList: any[];
  activeIndex: number;
  trackCtx: any;
  /**当前分类 */
  mainCategory?: CuisineCategory | CategoryType;
  useClientMode: boolean;
  handleMainCategory?: (item: CuisineCategory, nextLink?: string) => void;
  isNeedReplaceUrl: boolean;
  secondCategoryRef: any;
  buildPathName?: (item: ObjectType, isMainCategory: boolean) => string;
  sortFilterProps?: ObjectType;
}
const SKELETONS = [1, 1, 1, 1, 1, 1];
const MainCategory: FC<IProps> = ({
  loadingMain,
  mainCategoryList,
  activeIndex,
  trackCtx,
  mainCategory,
  useClientMode,
  handleMainCategory,
  isNeedReplaceUrl,
  secondCategoryRef,
  buildPathName,
  sortFilterProps
}) => {
  const { ellipseImpression } = useImpressionT2();
  const { ellipseClick } = useClickActionT2();
  const { lang } = useTranslation('common');

  const router = useRouter();
  const { query } = router;

  const genCategoryHref = (cateKey, isMainCategory, item?) => {
    let { sort, filters, slug, ...queries } = query;
    const { filterArr } = sortFilterProps;

    const newQuery: ObjectType = {
      ...queries,
      out_filters: {}
    };
    if (!!sort && !isMainCategory) newQuery.sort = sort;
    if (!filters) newQuery.filters = {};
    else {
      try {
        newQuery.filters = JSON.parse(filters as string);
      } catch (error) {
        newQuery.filters = {};
      }
    }
    let pathname = buildPathName?.(item, isMainCategory) || router.pathname;
    let fullUrl = '';
    if (isMainCategory) {
      if (pathname.startsWith('/cuisines')) {
        newQuery.filterCuisine = cateKey;
      } else if (pathname.startsWith('/category')) {
        newQuery.filter_sub_category = cateKey;
      }
      delete newQuery.filters.catalogue_num;
    } else {
      newQuery.filters.catalogue_num = cateKey;
    }
    if (Object.keys(newQuery.filters).length) {
      if (isMainCategory) {
        let _filter = {};
        Object.keys(newQuery.filters).forEach(key => {
          let isRemained = filterArr.findIndex(item => item.shareable && item.property_key === key) > -1;
          if (isRemained) {
            _filter[key] = newQuery.filters[key];
          }
        });
        if (Object.keys(_filter).length) {
          newQuery.filters = JSON.stringify(_filter);
        } else {
          delete newQuery.filters;
        }
      } else {
        newQuery.filters = JSON.stringify(newQuery.filters);
      }
    } else {
      delete newQuery.filters;
    }
    fullUrl = `${pathname}${queryToString(newQuery)}`;
    return fullUrl;
  };

  if (loadingMain || mainCategoryList?.length === 0) {
    return (
      <div className="w-full h-[94px] overflow-x-scroll">
        {SKELETONS.map((item, index) => {
          return (
            <div className="inline-block text-center first:ml-5 px-1 last:mr-5" key={`main-category-skeleton-${index}`}>
              <div className="w-[60px] h-[60px] rounded-full bg-surface-200-bg skeleton" />
              <div className="w-[60px] !h-4 !block !rounded-400 mt-1 bg-surface-200-bg skeleton" />
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <Swiper
      activeIndex={activeIndex}
      data-testid="wid-main-category"
      slidesPerView="auto"
      className="!pl-4 h-[94px] !pr-4"
      centeredSlides={true}
      centeredSlidesBounds={true}>
      {mainCategoryList.map((item, sub) => {
        const categoryIconItem = (
          <ImpTracker
            impTrackingT2={() => {
              let pageKey = getPageEventNameV2(getPathNoLangOnClient()) || '';
              ellipseImpression({
                mod_nm: CategoryMod.ellipse,
                mod_pos: 0,
                co: {
                  ellipse_label: item?.key,
                  ellipse_pos: sub,
                  ellipse_type: pageKey.replace('mweb_', ''),
                  is_new: item?.is_new,
                  url: router?.asPath
                },
                ctx: trackCtx
              });
            }}
            clickTrackingT2={() => {
              let pageKey = getPageEventNameV2(getPathNoLangOnClient()) || '';
              ellipseClick({
                mod_nm: CategoryMod.ellipse,
                mod_pos: 0,
                co: {
                  ellipse_label: item?.key,
                  ellipse_pos: sub,
                  ellipse_type: pageKey.replace('mweb_', ''),
                  url: router?.asPath
                },
                ctx: {
                  filter_sub_category: item?.key,
                  catalogue_num: router.query.catalogue_num || 'all',
                  sort: router.query.sort || 'recommend',
                  filters: router.query.filters ? JSON.parse(router.query.filters as string) : null,
                  ...trackCtx
                }
              });
            }}>
            <div data-testid={`btn-main-category-${item.num}`} className="inline-block">
              <div className="w-[60px] h-[60px] flex justify-center items-center touch-auto relative mx-auto my-0">
                <CroppedImage
                  url={item.key === mainCategory?.key ? item.active_img_url : item.img_url}
                  configs={{
                    renderWidth: 60,
                    renderHeight: 60
                  }}
                  width={60}
                  height={60}
                  alt="product image"
                  placeholder={<div className="w-[60px] h-[60px] bg-surface-200-bg skeleton rounded-full" />}
                />
                {item?.is_new && <NewTag className="top-1" />}
                {!isEmpty(item?.category_label) && <RewardsDealTag data={item?.category_label} />}
              </div>
              <div
                className={twMerge(
                  classnames(
                    'touch-auto text-center break-normal enki-body-2xs line-clamp-2 text-surface-100-fg-minor whitespace-normal mt-1',
                    {
                      '!leading-[120%]': lang === 'en',
                      ['text-surface-100-fg-default']: item.key === mainCategory?.key
                    }
                  )
                )}>
                {item.key === mainCategory?.key ? (
                  <h1 style={{ color: mainCategory?.color }} className="enki-body-2xs">
                    {item.name}
                  </h1>
                ) : (
                  item.name
                )}
              </div>
            </div>
          </ImpTracker>
        );
        //lunar year requirement
        if (+item.type === 4) {
          return (
            <SwiperSlide
              data-testid={`btn-main-category-${item.num}`}
              key={`main-category-${item.key}`}
              className="!w-auto max-w-[76px] box-border mx-1.5">
              <Link href={item.url}>
                <a className="flex flex-col items-center justify-center" aria-label={`search category ${item.key}`}>
                  {categoryIconItem}
                </a>
              </Link>
            </SwiperSlide>
          );
        }
        return (
          <SwiperSlide
            data-testid={`btn-main-category-${item.num}`}
            key={`main-category-${item.key}`}
            className="!w-auto max-w-[76px] box-border mx-1.5">
            {useClientMode ? (
              <a
                href="#"
                className="flex flex-col items-center justify-center"
                onClick={e => {
                  e.preventDefault();
                  handleMainCategory(item, genCategoryHref(item.key, true, item));
                  secondCategoryRef?.current?.scrollTo(0, 0);
                }}>
                {categoryIconItem}
              </a>
            ) : (
              <Link href={genCategoryHref(item.key, true, item) || ''} replace={isNeedReplaceUrl} shallow>
                <a
                  className="flex flex-col items-center justify-center"
                  onClick={() => {
                    handleMainCategory(item, genCategoryHref(item.key, true, item));
                    secondCategoryRef?.current?.scrollTo(0, 0);
                  }}
                  aria-label={`search category ${item?.key}`}>
                  {categoryIconItem}
                </a>
              </Link>
            )}
          </SwiperSlide>
        );
      })}
    </Swiper>
  );
};

export default MainCategory;
